import { useCallback } from 'react';
import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';
import { usePersistentGlobalState } from '#modules/utils/usePersistentState';
import useLiquidNobleApi from '#modules/utils/useLiquidNobleApi';

// Some mobile devices hang on the WebAuthn prompt and the browser promise
// never settles. Race it against a timer so callers can fall back to MFA
// instead of waiting forever. The underlying browser call cannot be
// cancelled, so a late response is simply discarded. Keep this long enough
// for a legitimate biometric attempt (the user needs time to answer the
// OS prompt); lower values make the MFA fallback appear sooner on hung
// devices but risk aborting real passkey attempts.
const PASSKEY_TIMEOUT_MS = 30000;

const withPasskeyTimeout = (promise) =>
  Promise.race([
    promise,
    new Promise((resolve) => {
      setTimeout(() => resolve(false), PASSKEY_TIMEOUT_MS);
    }),
  ]);

export default function usePasskey() {
  const api = useLiquidNobleApi();
  const [passkeyCredentialId, setPasskeyCredentialId] =
    usePersistentGlobalState('passkeyCredentialId', false);

  const registerPasskey = useCallback(
    async ({ userId, email, displayName, authToken }) => {
      // Registration requires an authenticated session. Right after login
      // the new token is not stored yet, so the caller passes it explicitly.
      const config = authToken
        ? { headers: { Authorization: `Bearer ${authToken}` } }
        : undefined;
      try {
        const res = await api.post(
          '/passkey/registration_options/',
          {
            user_id: userId,
            email,
            display_name: displayName,
          },
          config,
        );

        const attestationResponse = await withPasskeyTimeout(
          startRegistration(res.data),
        );
        if (!attestationResponse) {
          console.error('Passkey registration timed out');
          return false;
        }

        const verifyRes = await api.post(
          '/passkey/verify_registration/',
          {
            user_id: userId,
            attestation_response: attestationResponse,
          },
          config,
        );

        const { status, id } = verifyRes.data || {};
        if (status === 'ok') {
          setPasskeyCredentialId(id);
        }
        return status === 'ok';
      } catch (err) {
        console.error('Passkey registration failed:', err);
        return false;
      }
    },
    [api, setPasskeyCredentialId],
  );

  const isPasskeyAvailable = useCallback(
    async ({ userId }) => {
      if (!userId) {
        return false;
      }
      if (!passkeyCredentialId) {
        return false;
      }
      try {
        const res = await api.post('/passkey/is_available/', {
          user_id: userId,
          credential_id: passkeyCredentialId,
        });

        return !!res.data.available;
      } catch {
        return false;
      }
    },
    [api, passkeyCredentialId],
  );

  const getPasskeyAssertion = useCallback(
    async ({ userId } = {}) => {
      try {
        const res = await api.post('/passkey/authentication_options/', {
          user_id: userId,
          credential_id: passkeyCredentialId,
        });

        if (!res.data.available) {
          return false;
        }

        const assertionResponse = await withPasskeyTimeout(
          startAuthentication(res.data),
        );
        return assertionResponse;
      } catch (err) {
        console.error('Passkey login failed:', err);
        return false;
      }
    },
    [api, passkeyCredentialId],
  );

  return {
    registerPasskey,
    getPasskeyAssertion,
    isPasskeyAvailable,
  };
}
